import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Plus, Shield, Trash2, Edit, ChevronDown, LayoutGrid, BarChart2, FileText, ShoppingCart, MessageSquare, Settings, Users, Briefcase } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';

export function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [matrixRole, setMatrixRole] = useState(null); // for permission expansion
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        adminAPI.listRoles(),
        adminAPI.listPermissions(),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (editingRole) {
        await adminAPI.updateRole(editingRole.id, formData);
        toast.success('Role updated');
      } else {
        await adminAPI.createRole(formData);
        toast.success('Role created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await adminAPI.deleteRole(id);
      toast.success('Role deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || [],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: [] });
  };

  const togglePermission = (permName) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permName)
        ? prev.permissions.filter(p => p !== permName)
        : [...prev.permissions, permName]
    }));
  };

  const permissionGroups = useMemo(() => {
    const buckets = {
      dashboard: { title: 'Dashboards & BI', icon: LayoutGrid, resources: ['dashboard', 'dashboard_builder', 'analytics', 'performance', 'organization', 'kpis'] },
      crm: { title: 'CRM Workspace', icon: BarChart2, resources: ['opportunities', 'leads', 'accounts', 'activities', 'goals'] },
      revenue: { title: 'Revenue Ops', icon: FileText, resources: ['invoices', 'sales_orders'] },
      people: { title: 'Users & Structure', icon: Users, resources: ['users', 'roles', 'departments', 'rbac'] },
      operations: { title: 'Pipelines & Integrations', icon: ShoppingCart, resources: ['connections', 'mappings', 'pipelines', 'runs', 'integrations'] },
      communication: { title: 'Communication Center', icon: MessageSquare, resources: ['communication_center'] },
      settings: { title: 'Settings & Audit', icon: Settings, resources: ['settings', 'config', 'audit'] },
      other: { title: 'Other', icon: Briefcase, resources: [] },
    };
    const grouped = Object.values(buckets).reduce((acc, bucket) => ({ ...acc, [bucket.title]: { ...bucket, perms: [] } }), {});
    permissions.forEach((perm) => {
      const bucket = Object.values(buckets).find((entry) => entry.resources.includes(perm.resource)) || buckets.other;
      grouped[bucket.title].perms.push(perm);
    });
    return Object.values(grouped).filter(group => group.perms.length > 0);
  }, [permissions]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles</h1>
          <p className="text-gray-500">Manage roles and permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Sales Manager"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Manages sales team"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="border rounded-lg p-4 max-h-[50vh] overflow-y-auto">
                  <Tabs defaultValue={permissionGroups[0]?.title} className="space-y-4">
                    <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 rounded-xl p-1">
                      {permissionGroups.map((group) => {
                        const Icon = group.icon;
                        return <TabsTrigger key={group.title} value={group.title} className="rounded-lg text-xs"><Icon className="h-3.5 w-3.5 mr-1" />{group.title}</TabsTrigger>;
                      })}
                    </TabsList>
                    {permissionGroups.map((group) => {
                      const Icon = group.icon;
                      return (
                        <TabsContent key={group.title} value={group.title} className="space-y-3 mt-0">
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Icon className="h-4 w-4 text-[#800000]" /> {group.title}</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {group.perms.map((perm) => {
                              const checked = formData.permissions.includes(perm.name);
                              return (
                                <button
                                  type="button"
                                  key={perm.name}
                                  onClick={() => togglePermission(perm.name)}
                                  className={`text-left flex items-start gap-3 p-3 rounded-xl border transition-all ${checked ? 'border-[#800000] bg-[#800000]/5' : 'border-slate-200 hover:bg-slate-50'}`}
                                  data-testid={`rbac-permission-cell-${perm.name}`}
                                >
                                  <Checkbox checked={checked} className="mt-0.5 pointer-events-none" />
                                  <div>
                                    <p className="text-sm font-medium text-slate-800">{perm.description || perm.name}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">{perm.name}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </TabsContent>
                      );
                    })}
                  </Tabs>
                </div>
              </div>
            </div>
            <DialogFooter className="border-t pt-3 mt-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-[#800000] hover:bg-[#9a1919] text-white">{editingRole ? 'Update Role' : 'Create Role'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Users</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  No roles found
                </TableCell>
              </TableRow>
            ) : (
              roles.map((role) => (
                <TableRow key={role.id} data-testid={`rbac-role-row-${role.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-cyan-500" />
                      {role.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-500">
                    {role.description || '-'}
                  </TableCell>
                  <TableCell>
                    <button
                      className="flex items-center gap-1 hover:text-[#800000] transition-colors group"
                      onClick={() => setMatrixRole(matrixRole === role.id ? null : role.id)}
                      data-testid={`role-permissions-${role.id}`}
                    >
                      <Badge variant="secondary" className="group-hover:bg-[#800000]/10">
                        {role.permissions?.length || 0} permissions
                      </Badge>
                      <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${matrixRole === role.id ? 'rotate-180' : ''}`} />
                    </button>
                    {/* Inline permission list */}
                    {matrixRole === role.id && role.permissions?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1 max-w-xs">
                        {role.permissions.slice(0,20).map(p => (
                          <span key={p} className="text-[9px] bg-[#800000]/10 text-[#800000] px-1.5 py-0.5 rounded">{p}</span>
                        ))}
                        {role.permissions.length > 20 && <span className="text-[9px] text-muted-foreground">+{role.permissions.length - 20} more</span>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {role.users_count || 0} users
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(role)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(role.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
